// Pure helpers for viewing Site Progress 3D deliverables (model + point cloud)
// in the browser. Backend decides which links are viewable (site_progress.py
// view_format/probe_view); this file parses and lines files up for compare.

export const Z_UP_FORMATS = ['las', 'ply', 'pcd', 'obj']; // ODM/WebODM exports are Z-up; glTF is Y-up
export const MAX_FILE_BYTES = 300 * 1024 * 1024;

export function maxPoints(coarse) {
  return coarse ? 1500000 : 4000000;
}

export function viewFrames(visits, kind) {
  const out = [];
  (visits || []).forEach((v) => {
    const d = (v.deliverables || []).find((x) => x.kind === kind && x.view);
    if (d && v.date) out.push({ visitId: v.id, date: v.date, url: d.url, format: d.view.format, kind, label: d.label });
  });
  out.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  return out;
}

export function formatBytes(n) {
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n >= 1073741824) return `${(n / 1073741824).toFixed(1)} GB`;
  if (n >= 1048576) return `${Math.round(n / 1048576)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
}

export function progressLine(loaded, total) {
  if (total > 0) return `Loading ${Math.min(100, Math.floor((loaded / total) * 100))}%`;
  return loaded > 0 ? `Loading ${formatBytes(loaded)}` : 'Loading';
}

// Byte offset of R,G,B (uint16 each) inside a LAS point record, by format.
const LAS_RGB_OFFSET = { 2: 20, 3: 28, 5: 28, 7: 30, 8: 30, 10: 30 };

export class LasError extends Error {}

// Parse an uncompressed LAS 1.0–1.4 file (point formats 0–10). Returns
// positions relative to the footprint centre at ground level (so UTM-sized
// coordinates keep float32 precision), Z-up, every Nth point to stay under
// maxPts. Colours are 0–1 floats, or null when the format carries no RGB.
export function parseLAS(buffer, maxPts = 4000000) {
  if (!buffer || buffer.byteLength < 227) throw new LasError('Not a LAS file.');
  const dv = new DataView(buffer);
  const sig = String.fromCharCode(dv.getUint8(0), dv.getUint8(1), dv.getUint8(2), dv.getUint8(3));
  if (sig !== 'LASF') throw new LasError('Not a LAS file.');
  const minor = dv.getUint8(25);
  const offsetToPoints = dv.getUint32(96, true);
  const rawFormat = dv.getUint8(104);
  if (rawFormat & 0x80 || rawFormat & 0x40) throw new LasError('Compressed (LAZ) point clouds need a .las or .ply export.');
  const format = rawFormat & 0x3f;
  if (format > 10) throw new LasError(`Unsupported LAS point format ${format}.`);
  const recLen = dv.getUint16(105, true);
  let count = dv.getUint32(107, true);
  if (minor >= 4 && buffer.byteLength >= 255) {
    const big = Number(dv.getBigUint64(247, true));
    if (big > 0) count = big;
  }
  const sx = dv.getFloat64(131, true);
  const sy = dv.getFloat64(139, true);
  const sz = dv.getFloat64(147, true);
  const ox = dv.getFloat64(155, true);
  const oy = dv.getFloat64(163, true);
  const oz = dv.getFloat64(171, true);
  const maxX = dv.getFloat64(179, true);
  const minX = dv.getFloat64(187, true);
  const maxY = dv.getFloat64(195, true);
  const minY = dv.getFloat64(203, true);
  const minZ = dv.getFloat64(219, true);
  if (!recLen || recLen < 20) throw new LasError('Damaged LAS header.');
  const available = Math.floor((buffer.byteLength - offsetToPoints) / recLen);
  const total = Math.max(0, Math.min(count, available));
  if (!total) throw new LasError('The point cloud is empty.');
  const step = Math.max(1, Math.ceil(total / Math.max(1, maxPts)));
  const n = Math.ceil(total / step);
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const cz = minZ;
  const positions = new Float32Array(n * 3);
  const rgbOff = LAS_RGB_OFFSET[format];
  const raw = rgbOff !== undefined && rgbOff + 6 <= recLen ? new Uint16Array(n * 3) : null;
  let peak = 0;
  for (let i = 0, k = 0; i < total && k < n; i += step, k += 1) {
    const at = offsetToPoints + i * recLen;
    positions[k * 3] = dv.getInt32(at, true) * sx + ox - cx;
    positions[k * 3 + 1] = dv.getInt32(at + 4, true) * sy + oy - cy;
    positions[k * 3 + 2] = dv.getInt32(at + 8, true) * sz + oz - cz;
    if (raw) {
      const r = dv.getUint16(at + rgbOff, true);
      const g = dv.getUint16(at + rgbOff + 2, true);
      const b = dv.getUint16(at + rgbOff + 4, true);
      raw[k * 3] = r; raw[k * 3 + 1] = g; raw[k * 3 + 2] = b;
      if (r > peak) peak = r;
      if (g > peak) peak = g;
      if (b > peak) peak = b;
    }
  }
  let colors = null;
  if (raw && peak > 0) {
    const div = peak > 255 ? 65535 : 255; // some writers store 8-bit values in the 16-bit fields
    colors = new Float32Array(n * 3);
    for (let j = 0; j < raw.length; j += 1) colors[j] = raw[j] / div;
  }
  return { positions, colors, count: n, total, step, format, origin: [cx, cy, cz], bounds: { minX, maxX, minY, maxY, minZ } };
}

// Grey-to-off-white ramp by height for clouds without RGB (positions Z-up).
export function heightColors(positions) {
  const n = positions.length / 3;
  let lo = Infinity;
  let hi = -Infinity;
  for (let i = 0; i < n; i += 1) {
    const z = positions[i * 3 + 2];
    if (z < lo) lo = z;
    if (z > hi) hi = z;
  }
  const span = hi - lo || 1;
  const out = new Float32Array(n * 3);
  for (let i = 0; i < n; i += 1) {
    const t = (positions[i * 3 + 2] - lo) / span;
    const v = 0.35 + 0.6 * t;
    out[i * 3] = v; out[i * 3 + 1] = v * 0.98; out[i * 3 + 2] = v * 0.94;
  }
  return out;
}
